/**
 * A file's Share dialog: post it to another conversation (with an optional message), copy its internal link, and —
 * when the workspace allows it (`SharingSettings.publicFiles`, default admins only) — a public link with an expiry,
 * revocable. Guests without `downloadFiles` can do neither (the server refuses too). Online only.
 */
import type { FileLink, FileSharing } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { expiryOptions, fileLinkBlockedReason, shareTargets, type ExpiryOption, type ShareTarget } from "../logic/sharing.ts";
import { errorKind, type SharingApi } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type FileShareState = {
  status: "loading" | "ready" | "error" | "notFound";
  fileId: string;
  canShare: boolean;
  canManageLink: boolean;
  /** Why the public link is unavailable to me. */
  linkBlockedReason?: MessageDescriptor;
  link?: FileLink;
  linkExpiry: ExpiryOption["key"];
  expiryOptions: ExpiryOption[];
  linkBusy: boolean;
  query: string;
  targets: ShareTarget[];
  target?: ShareTarget;
  message: string;
  sending: boolean;
  error?: MessageDescriptor;
  notice?: MessageDescriptor;
};

const TARGET_LIMIT = 8;

export class FileShareViewModel extends ViewModel<FileShareState> {
  private readonly ws: WorkspaceViewModel;
  private readonly api: SharingApi;

  constructor(ws: WorkspaceViewModel, api: SharingApi, fileId: string) {
    super({
      status: "loading",
      fileId,
      canShare: false,
      canManageLink: false,
      linkExpiry: "never",
      expiryOptions: expiryOptions(ws.deps.clock.now()),
      linkBusy: false,
      query: "",
      targets: [],
      message: "",
      sending: false,
    });
    this.ws = ws;
    this.api = api;
    this.onDispose(ws.subscribe(() => this.update({ targets: this.targets(this.state.query) })));
    void this.refresh();
  }

  private apply(s: FileSharing): void {
    const next: FileShareState = { ...this.state, status: "ready", canShare: s.canShare, canManageLink: s.canManageLink, targets: this.targets(this.state.query) };
    const reason = fileLinkBlockedReason(s);
    if (reason === undefined) delete next.linkBlockedReason;
    else next.linkBlockedReason = reason;
    if (s.link === undefined) delete next.link;
    else next.link = s.link;
    this.setState(next);
  }

  async refresh(): Promise<void> {
    try {
      this.apply(await this.api.fileSharing(this.ws.wsId, this.state.fileId));
    } catch (e) {
      this.update({ status: errorKind(e) === "notFound" ? "notFound" : "error" });
    }
  }

  private targets(query: string): ShareTarget[] {
    const c = this.ws.state.cache;
    if (c === undefined) return [];
    return shareTargets(Object.values(c.channels), c.memberships, c.members, c.meId, query, TARGET_LIMIT);
  }

  setQuery(query: string): void {
    this.update({ query, targets: this.targets(query) });
  }

  choose(target: ShareTarget | undefined): void {
    const next = { ...this.state, query: "" };
    if (target === undefined) delete next.target;
    else next.target = target;
    next.targets = this.targets("");
    this.setState(next);
  }

  setMessage(message: string): void {
    this.update({ message });
  }

  /** Posts the file to the chosen conversation. Resolves with its id (to open it), or undefined on failure. */
  async send(): Promise<string | undefined> {
    const target = this.state.target;
    if (target === undefined || !this.state.canShare || this.state.sending) return undefined;
    this.update({ sending: true });
    try {
      const text = this.state.message.trim();
      await this.api.shareFile(this.ws.wsId, this.state.fileId, { channelId: target.channelId, clientId: this.ws.deps.ids.next(), ...(text === "" ? {} : { text }) });
      const next = { ...this.state, sending: false, message: "", notice: msg("files.share.sent", { name: target.isDm ? target.label : `#${target.label}` }) };
      delete next.target;
      delete next.error;
      this.setState(next);
      return target.channelId;
    } catch {
      this.update({ sending: false, error: msg("files.share.error") });
      return undefined;
    }
  }

  setLinkExpiry(key: ExpiryOption["key"]): void {
    this.update({ linkExpiry: key });
    if (this.state.link !== undefined) void this.createLink();
  }

  async createLink(): Promise<void> {
    if (!this.state.canManageLink || this.state.linkBusy) return;
    this.update({ linkBusy: true });
    const expiresAt = this.state.expiryOptions.find((o) => o.key === this.state.linkExpiry)?.expiresAt ?? null;
    try {
      this.apply(await this.api.createFileLink(this.ws.wsId, this.state.fileId, { expiresAt }));
      this.update({ linkBusy: false });
    } catch {
      this.update({ linkBusy: false, error: msg("files.share.error") });
    }
  }

  async revokeLink(): Promise<void> {
    if (this.state.linkBusy) return;
    this.update({ linkBusy: true });
    try {
      this.apply(await this.api.revokeFileLink(this.ws.wsId, this.state.fileId));
      this.update({ linkBusy: false });
    } catch {
      this.update({ linkBusy: false, error: msg("files.share.error") });
    }
  }

  copied(): void {
    this.update({ notice: msg("sharing.copied") });
  }

  dismissNotice(): void {
    const s = { ...this.state };
    delete s.notice;
    delete s.error;
    this.setState(s);
  }
}
